import { Suspense, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import './Layout.css';
import { useAuth } from '../lib/authStore.tsx';
import { strings } from '../lib/strings.tsx';
import { NAV_ICON } from './navIcons.tsx';
import { ErrorBoundary } from './ErrorBoundary.tsx';

const NAV = [
  /*
   * ★ **차례: Dashboard → Pipeline Operator → Pipeline Builder → 나머지** (운영자 지시 2026-09-14 21:1x KST).
   *   같은 날 앞서 «Operator 맨 위» 였다가, Dashboard(첫 화면)가 생기며 그 위로 올라갔다.
   * ⚠ 번호(⑪·⑩·①)는 **안 바꿨다** — 번호는 자리가 아니라 문서가 화면을 부르는 이름이다(아래 `navName` 주석).
   *   번호를 자리에 맞춰 다시 매기면 03·06 의 "⑦ Settings" 같은 참조가 전부 어긋난다.
   */
  { to: '/dashboard', label: strings.nav.dashboard },
  { to: '/operator', label: strings.nav.operator },
  { to: '/pipelines', label: strings.nav.pipelines },
  { to: '/runs', label: strings.nav.runs },
  { to: '/agent-io', label: strings.nav.agentIo },
  { to: '/approvals', label: strings.nav.approvals },
  { to: '/fleet', label: strings.nav.fleet },
  /* 지시220 — 플릿 곁에 둔다: 기계 다음에 그 기계의 데이터베이스다. */
  { to: '/databases', label: strings.nav.databases },
  { to: '/sessions', label: strings.nav.sessions },
  { to: '/settings', label: strings.nav.settings },
  { to: '/resources', label: strings.nav.resources },
  { to: '/access', label: strings.nav.access },
];

/**
 * 라벨에서 번호 표식(`① `)을 뗀 이름 (지시36 §4).
 *
 * 번호는 문서가 화면을 부르는 이름이라(03·06 이 "⑦ Settings" 로 참조한다)
 * **버리지 않고 툴팁에 남긴다.** 화면에는 아이콘 + 이름만 둔다 — 아이콘 옆에
 * 번호까지 붙이면 셋이 겹쳐 오히려 읽기 어렵다.
 */
function navName(label: string): string {
  const [first, ...rest] = label.split(' ');
  return rest.length > 0 && first !== undefined && !/[A-Za-z]/.test(first) ? rest.join(' ') : label;
}

/** 사이드바 접힘 선택을 기억하는 곳(지시22 §4). API 키처럼 비밀이라 localStorage를
 * 피한 게 아니라(결정46) 이건 순수 UI 선호이므로 그 방침과 무관하다 — 파이프라인
 * 화면과 같은 이유로, 매번 다시 접게 하지 않기 위해 저장한다. */
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'console:sidebar-collapsed';

function readStoredSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function storeSidebarCollapsed(value: boolean) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, value ? '1' : '0');
  } catch {
    // 접근이 막혀도 접기 자체는 계속 동작해야 한다 — 기억만 못 할 뿐.
  }
}

export function Layout() {
  const { me, logout } = useAuth();
  const { pathname } = useLocation();
  const [collapsed, setCollapsedState] = useState(readStoredSidebarCollapsed);
  function setCollapsed(value: boolean) {
    setCollapsedState(value);
    storeSidebarCollapsed(value);
  }

  return (
    <div className="layout">
      <nav className={collapsed ? 'layout__nav layout__nav--collapsed' : 'layout__nav'}>
        <button
          type="button"
          className="icon-button layout__sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? strings.layout.expandSidebar : strings.layout.collapseSidebar}
          title={collapsed ? strings.layout.expandSidebar : strings.layout.collapseSidebar}
        >
          {collapsed ? '»' : '«'}
        </button>

        {/* 마크 + 워드마크 + 제품명. 워드마크는 텍스트로 둔다 — 테마·확대에 강하고
            폰트만 정하면 되며, 이미지로 박으면 다크에서 다시 안 보이게 된다. 접힌
            상태에선 마크만 남기고 워드마크·제품명 텍스트는 감춘다(자리가 없음). */}
        <div className="layout__brand">
          <span className="layout__mark" role="img" aria-label={strings.layout.brand} />
          {!collapsed && <p className="layout__wordmark">{strings.layout.brand}</p>}
        </div>
        {!collapsed && <p className="layout__product">{strings.layout.product}</p>}

        <ul className="layout__links">
          {NAV.map((item) => {
            const Icon = NAV_ICON[item.to];
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    isActive ? 'layout__link layout__link--active' : 'layout__link'
                  }
                  // 접었을 때는 이름이 안 보이므로 툴팁이 유일한 단서다(지시36 §4).
                  // 펼쳤을 때도 번호 표식을 알 수 있게 원래 라벨을 그대로 쓴다.
                  title={item.label}
                  /*
                   * ★ **접으면 이름이 통째로 사라진다** (결정2472).
                   *
                   * 위 주석은 *"접었을 때는 툴팁이 유일한 단서다"* 라고 한다. 눈으로는
                   * 맞지만 **읽어 주는 화면에는 아니다** 〔실측 08-30〕: 접은 채로 재니
                   * 링크 아홉이 **하나도 이름으로 안 잡혔다**
                   * (`getAllByRole('link', { name })` → 0/9). 아이콘이 `aria-hidden` 이라
                   * 이름이 될 것이 없고 `title` 만으로는 안 잡혔다.
                   * ⚠ 도구 탓이 아니다 — `title` 만 있는 링크를 따로 만들어 재니 잡힌다.
                   *
                   * 그래서 **접었을 때만** 이름을 박는다. 펼쳤을 때 박으면 보이는 글자
                   * (`navName`)와 이름이 갈려 «보이는 이름과 읽는 이름이 다른» 자리가 된다.
                   *
                   * ⚠ 이름은 `item.label`(`② Runs`)이 아니라 **`navName`(`Runs`)** 이다.
                   * 번호 표식은 **눈으로 찾으라고** 붙인 것이지(지시36 §4) 읽으라고 붙인
                   * 것이 아니다. 그리고 펼쳤을 때 보이는 글자가 `navName` 이라, 이렇게 해야
                   * **접든 펴든 이름이 같다.**
                   */
                  {...(collapsed ? { 'aria-label': navName(item.label) } : {})}
                >
                  {Icon !== undefined && <Icon />}
                  {!collapsed && <span className="layout__link-name">{navName(item.label)}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <div className="layout__footer">
          {/* 기계용 키 발급/폐기는 ⑦ 설정에 이미 있음 — 콘솔은 API 키를 다루지 않는다(결정46). */}
          {!collapsed && me?.ownerId !== undefined && (
            <p className="layout__whoami">{strings.layout.loggedInAs(me.ownerId)}</p>
          )}
          <button
            type="button"
            onClick={() => void logout()}
            style={{ fontSize: 12 }}
            title={collapsed ? strings.layout.logout : undefined}
            aria-label={collapsed ? strings.layout.logout : undefined}
          >
            {collapsed ? '⏻' : strings.layout.logout}
          </button>
        </div>
      </nav>
      <main className="layout__main">
        {/*
         * 한 화면이 터져도 **왼쪽 길은 살려 둔다** (결정2403).
         *
         * ⚠ `key` 가 핵심이다. 오류 경계는 **스스로 풀리지 않는다** — 키가 없으면
         * 다른 메뉴를 눌러도 같은 오류 화면이 그대로 남아, 길을 살려 둔 것이
         * **아무 소용이 없어진다.** 경로가 바뀌면 다시 마운트되어 풀린다.
         */}
        <ErrorBoundary key={pathname}>
          {/*
           * 화면을 늦게 싣는다(지시185) — 기다림은 **이 칸 안에서만** 보인다. 바깥(App)의 경계만 있으면
           * 메뉴를 누를 때마다 왼쪽 길까지 «Loading» 한 줄로 바뀌었다가 돌아온다.
           */}
          <Suspense fallback={<div style={{ padding: 24 }}>{strings.app.loading}</div>}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}
